/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Analytics.tsx
 * DESCRIPTION: Internal analytics and statistics page for administrators.
 *              Provides comprehensive insights into platform usage with
 *              tab-based organization for optimal screen space utilization.
 *
 * FEATURES:
 * - Admin-only access with authentication
 * - Tab-based navigation (Overview, Volume & Risk, AI & Users)
 * - Interactive charts with real-time data
 * - Sortable tables
 * - Integrated filters per section
 * - Export and reporting functionality
 * - Dark/Light mode support
 *
 * LAYOUT STRUCTURE:
 * - Overview Tab: 4 metric cards + AI Provider Performance + File Formats + Creation Tools + Export section
 * - Volume & Risk Tab: Upload Volume chart + Trust Score Distribution + Risk Categories
 * - AI & Users Tab: AI Assessment Trends + User/Organization Activity
 *
 * DATA INTEGRATION:
 * - Mock data structured for easy API replacement
 * - All charts functional with tooltips
 * - All tables sortable
 * - Filters integrated into relevant cards
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 3.0.0
 * @created 2026-03-26
 * @updated 2026-03-27 - Reorganized with tabs, integrated filters, exact layout match
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import {
  Lock,
  Unlock,
  Download,
  Calendar,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  Shield,
  Users,
  FilePieChart,
  Settings,
  LogOut,
  Key,
  X,
  Eye,
  EyeOff,
  MoreVertical,
  Filter,
  ChevronDown,
  SortAsc,
  SortDesc,
  Copy,
  Check,
  Mail,
  Share2,
  Clock,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface UploadVolumeData {
  time: string;
  video: number;
  image: number;
  audio: number;
}

interface AIAssessmentData {
  time: string;
  mistral: number;
  qwen: number;
  openai: number;
  responseTime: number;
}

interface TrustScoreData {
  range: string;
  count: number;
  percentage: number;
}

interface RiskCategoryData {
  category: string;
  percentage: number;
  count: number;
}

interface UserData {
  user: string;
  uploads: number;
  avgTrustScore: number;
  riskAlerts: number;
  lastActive: string;
}

interface ProviderPerformance {
  provider: string;
  requests: number;
  avgResponseTime: number;
  successRate: number;
  tokenUsage: string;
  estCost: number;
  status: 'optimal' | 'degraded' | 'issues';
  trend: number[];
}

interface FileFormatData {
  format: string;
  count: number;
  percentage: number;
  color: string;
}

interface CreationToolData {
  tool: string;
  count: number;
  percentage: number;
}

type SortField = 'user' | 'uploads' | 'avgTrustScore' | 'riskAlerts' | 'lastActive';
type SortOrder = 'asc' | 'desc';
type ContentType = 'all' | 'video' | 'image' | 'audio';
type TimeAggregation = 'daily' | 'weekly' | 'monthly';

// ─────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_UPLOAD_VOLUME: UploadVolumeData[] = [
  { time: '00:00', video: 200, image: 350, audio: 120 },
  { time: '04:00', video: 150, image: 280, audio: 90 },
  { time: '08:00', video: 420, image: 580, audio: 200 },
  { time: '12:00', video: 680, image: 820, audio: 350 },
  { time: '16:00', video: 550, image: 720, audio: 280 },
  { time: '20:00', video: 380, image: 520, audio: 180 },
  { time: '23:59', video: 280, image: 420, audio: 140 },
];

const MOCK_AI_ASSESSMENTS: AIAssessmentData[] = [
  { time: '00:00', mistral: 300, qwen: 250, openai: 200, responseTime: 245 },
  { time: '04:00', mistral: 180, qwen: 150, openai: 120, responseTime: 198 },
  { time: '08:00', mistral: 520, qwen: 450, openai: 350, responseTime: 312 },
  { time: '12:00', mistral: 780, qwen: 680, openai: 520, responseTime: 385 },
  { time: '16:00', mistral: 650, qwen: 580, openai: 450, responseTime: 342 },
  { time: '20:00', mistral: 480, qwen: 420, openai: 320, responseTime: 298 },
  { time: '23:59', mistral: 350, qwen: 300, openai: 230, responseTime: 256 },
];

const MOCK_TRUST_DISTRIBUTION: TrustScoreData[] = [
  { range: '0.0-0.2', count: 125, percentage: 8 },
  { range: '0.2-0.4', count: 245, percentage: 15 },
  { range: '0.4-0.6', count: 380, percentage: 23 },
  { range: '0.6-0.7', count: 320, percentage: 19 },
  { range: '0.7-0.8', count: 285, percentage: 17 },
  { range: '0.8-0.9', count: 210, percentage: 13 },
  { range: '0.9-1.0', count: 82, percentage: 5 },
];

const MOCK_RISK_CATEGORIES: RiskCategoryData[] = [
  { category: 'Security', percentage: 45, count: 21 },
  { category: 'Copyright', percentage: 38, count: 18 },
  { category: 'Authenticity', percentage: 32, count: 15 },
  { category: 'Privacy', percentage: 18, count: 8 },
  { category: 'Policy', percentage: 12, count: 6 },
];

const MOCK_USER_DATA: UserData[] = [
  { user: 'Photoshop', uploads: 1253, avgTrustScore: 0.85, riskAlerts: 1, lastActive: '2026-03-28' },
  { user: 'Final Cut', uploads: 289, avgTrustScore: 0.83, riskAlerts: 2, lastActive: '2026-03-28' },
  { user: 'FFmpeg', uploads: 276, avgTrustScore: 0.83, riskAlerts: 0, lastActive: '2026-03-28' },
  { user: 'Premiere Pro', uploads: 245, avgTrustScore: 0.81, riskAlerts: 3, lastActive: '2026-03-27' },
  { user: 'DaVinci Resolve', uploads: 198, avgTrustScore: 0.79, riskAlerts: 1, lastActive: '2026-03-27' },
  { user: 'After Effects', uploads: 167, avgTrustScore: 0.77, riskAlerts: 0, lastActive: '2026-03-26' },
  { user: 'GIMP', uploads: 134, avgTrustScore: 0.72, riskAlerts: 2, lastActive: '2026-03-26' },
  { user: 'Canva', uploads: 112, avgTrustScore: 0.68, riskAlerts: 1, lastActive: '2026-03-25' },
];

const MOCK_PROVIDER_PERFORMANCE: ProviderPerformance[] = [
  {
    provider: 'Mistral AI',
    requests: 651,
    avgResponseTime: 8.70,
    successRate: 95,
    tokenUsage: '1.12B',
    estCost: 3000,
    status: 'optimal',
    trend: [8.2, 8.5, 8.7, 8.9, 8.7, 8.6],
  },
  {
    provider: 'Qwen',
    requests: 524,
    avgResponseTime: 12.3,
    successRate: 92,
    tokenUsage: '0.98B',
    estCost: 2450,
    status: 'optimal',
    trend: [11.8, 12.1, 12.5, 12.8, 12.3, 12.2],
  },
  {
    provider: 'OpenAI',
    requests: 412,
    avgResponseTime: 15.6,
    successRate: 88,
    tokenUsage: '0.76B',
    estCost: 3800,
    status: 'degraded',
    trend: [14.2, 15.1, 16.8, 17.2, 15.6, 15.4],
  },
];

const MOCK_FILE_FORMATS: FileFormatData[] = [
  { format: 'MP4', count: 4250, percentage: 33, color: '#3b82f6' },
  { format: 'MOV', count: 2840, percentage: 22, color: '#10b981' },
  { format: 'JPEG', count: 2150, percentage: 17, color: '#f59e0b' },
  { format: 'PNG', count: 1680, percentage: 13, color: '#8b5cf6' },
  { format: 'MP3', count: 980, percentage: 8, color: '#ec4899' },
  { format: 'WAV', count: 520, percentage: 4, color: '#06b6d4' },
  { format: 'Other', count: 427, percentage: 3, color: '#6b7280' },
];

const MOCK_CREATION_TOOLS: CreationToolData[] = [
  { tool: 'Photoshop', count: 2840, percentage: 22 },
  { tool: 'Final Cut', count: 1950, percentage: 15 },
  { tool: 'FFmpeg', count: 1680, percentage: 13 },
  { tool: 'Premiere Pro', count: 1420, percentage: 11 },
  { tool: 'AI Tools', count: 980, percentage: 8 },
  { tool: 'DaVinci Resolve', count: 750, percentage: 6 },
  { tool: 'After Effects', count: 620, percentage: 5 },
  { tool: 'Others', count: 2560, percentage: 20 },
];

const ADMIN_CREDENTIALS = {
  email: 'admin@verimark.ai',
  password: 'admin2026',
};

// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatCurrency = (num: number): string => {
  return `$${num.toLocaleString()}`;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Analytics: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user, isLoaded } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const [activeTab, setActiveTab] = useState<'overview' | 'volume-risk' | 'ai-users'>('overview');
  const [sortField, setSortField] = useState<SortField>('uploads');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [contentType, setContentType] = useState<ContentType>('all');
  const [selectedProviders, setSelectedProviders] = useState<string[]>(['mistral', 'qwen', 'openai']);
  const [userOrgFilter, setUserOrgFilter] = useState<string>('all');
  const [timeAggregation, setTimeAggregation] = useState<TimeAggregation>('daily');

  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded) {
      checkAdminAccess();
    }
  }, [isLoaded, user]);

  useEffect(() => {
    const loadData = async () => {
      if (isAdmin) {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 500));
        setIsLoading(false);
      }
    };
    loadData();
  }, [isAdmin]);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const checkAdminAccess = () => {
    const userMetadata = user?.publicMetadata as { role?: string } | undefined;
    if (userMetadata?.role === 'admin') {
      setIsAdmin(true);
      setShowLoginModal(false);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (adminEmail === ADMIN_CREDENTIALS.email && adminPassword === ADMIN_CREDENTIALS.password) {
      setIsAdmin(true);
      setShowLoginModal(false);
      setAdminEmail('');
      setAdminPassword('');
    } else {
      setLoginError('Invalid admin credentials');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    setShowLoginModal(true);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedUserData = [...MOCK_USER_DATA].sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'user':
        comparison = a.user.localeCompare(b.user);
        break;
      case 'uploads':
        comparison = a.uploads - b.uploads;
        break;
      case 'avgTrustScore':
        comparison = a.avgTrustScore - b.avgTrustScore;
        break;
      case 'riskAlerts':
        comparison = a.riskAlerts - b.riskAlerts;
        break;
      case 'lastActive':
        comparison = new Date(a.lastActive).getTime() - new Date(b.lastActive).getTime();
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const handleExport = (format: 'csv' | 'json' | 'pdf') => {
    alert(`Exporting analytics data as ${format.toUpperCase()}...`);
  };

  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/sign-in';
    });
  };

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Admin Login Modal
  // ───────────────────────────────────────────────────────────────────
  if (!isAdmin && showLoginModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Admin Access Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This page contains sensitive analytics data.
            </p>
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Email
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="admin@verimark.ai"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{loginError}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Lock className="w-5 h-5" />
              Authenticate
            </button>

            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Demo Credentials:</strong><br />
              Email: admin@verimark.ai<br />
              Password: Send a request 
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────
  // RENDER - Main Analytics Page
  // ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-8">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Analytics</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Internal statistics and platform insights
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg">
              <Unlock className="w-5 h-5" />
              <span className="text-sm font-medium">Admin Access</span>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm font-medium">Exit Admin</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'volume-risk', label: 'Upload Volume & Risk', icon: TrendingUp },
            { id: 'ai-users', label: 'AI Assessment & Users', icon: Users },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <>
            {/* Top 4 Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Media Processed</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">12,847</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      +12% vs last period
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Activity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="mt-2">
                  <ResponsiveContainer width="100%" height={40}>
                    <LineChart data={MOCK_UPLOAD_VOLUME.slice(-5)}>
                      <Line type="monotone" dataKey="video" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Trust Score</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">0.87</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                      +0.05 vs last period
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Shield className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                </div>
                <div className="mt-2">
                  <ResponsiveContainer width="100%" height={40}>
                    <BarChart data={MOCK_TRUST_DISTRIBUTION.slice(-5)}>
                      <Bar dataKey="percentage" fill="#10b981" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">AI Assessments Completed</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">9,234</p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">
                      +18% vs last period
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-medium rounded">
                    Mistral
                  </span>
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-medium rounded">
                    Qwen
                  </span>
                  <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium rounded">
                    OpenAI
                  </span>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk Alerts Triggered</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">47</p>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                      +3 vs last period
                    </p>
                  </div>
                  <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex gap-2 mt-4 text-sm">
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">Low: 28</span>
                  <span className="text-yellow-600 dark:text-yellow-400 font-medium">Med: 15</span>
                  <span className="text-red-600 dark:text-red-400 font-medium">High: 4</span>
                </div>
              </div>
            </div>

            {/* AI Provider Performance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Provider Performance</h3>
                <MoreVertical className="w-5 h-5 text-gray-400" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Provider</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Requests</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Avg Response Time</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Success Rate</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Token Usage</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Est. Cost</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Trends</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Status</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Quick Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {MOCK_PROVIDER_PERFORMANCE.map((provider) => (
                      <tr key={provider.provider} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">{provider.provider}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{provider.requests}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{provider.avgResponseTime}s</td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{provider.successRate}%</td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{provider.tokenUsage}</td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{formatCurrency(provider.estCost)}</td>
                        <td className="py-3 px-4">
                          <div className="w-20 h-8">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={provider.trend.map((v, i) => ({ i, v }))}>
                                <Line type="monotone" dataKey="v" stroke={provider.status === 'optimal' ? '#10b981' : '#f59e0b'} strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            provider.status === 'optimal'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {provider.status.charAt(0).toUpperCase() + provider.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium">
                            Set as Default
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* File Formats & Creation Tools */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* File Format Analysis */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">File Format Analysis</h3>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                      value={contentType}
                      onChange={(e) => setContentType(e.target.value as ContentType)}
                      className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Types</option>
                      <option value="video">Video</option>
                      <option value="image">Image</option>
                      <option value="audio">Audio</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie
                        data={MOCK_FILE_FORMATS}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ format, percentage }) => `${percentage}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="percentage"
                      >
                        {MOCK_FILE_FORMATS.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {MOCK_FILE_FORMATS.map((format) => (
                      <div key={format.format} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: format.color }} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex-1">{format.format}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{format.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Creation Tool Detection */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Creation Tool Detection</h3>
                  <MoreVertical className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-4 mb-6">
                  {MOCK_CREATION_TOOLS.slice(0, 5).map((tool) => (
                    <div key={tool.tool}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{tool.tool}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{tool.percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-emerald-500 h-3 rounded-full transition-all"
                          style={{ width: `${tool.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-8 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <ResponsiveContainer width={120} height={120}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'AI-Generated', value: 35, color: '#3b82f6' },
                          { name: 'Human-Created', value: 65, color: '#10b981' },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ value }) => `${value}%`}
                        outerRadius={50}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        <Cell fill="#3b82f6" />
                        <Cell fill="#10b981" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Content Origin</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Confidence threshold &lt;78%</p>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">AI-Generated</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">Human-Created</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Export & Reporting Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Export & Reporting</h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleExport('csv')}
                    className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport('json')}
                    className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    JSON
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                  >
                    PDF Report
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Schedule Report</h3>
                <div className="flex items-center gap-3">
                  <select className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500">
                    <option>Daily</option>
                    <option>Weekly</option>
                    <option>Monthly</option>
                  </select>
                  <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium">
                    <Mail className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Share Dashboard</h3>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value="public-link.verimark.ai/analytics"
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('public-link.verimark.ai/analytics');
                      alert('Link copied!');
                    }}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* VOLUME & RISK TAB */}
        {activeTab === 'volume-risk' && (
          <>
            {/* Upload Volume Over Time */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Upload Volume Over Time</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {(['daily', 'weekly', 'monthly'] as const).map((agg) => (
                      <button
                        key={agg}
                        onClick={() => setTimeAggregation(agg)}
                        className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                          timeAggregation === agg
                            ? 'bg-white dark:bg-gray-600 shadow-sm'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {agg}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={MOCK_UPLOAD_VOLUME}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                  <YAxis stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="video" stroke="#3b82f6" strokeWidth={2} dot={false} name="Video" />
                  <Line type="monotone" dataKey="image" stroke="#10b981" strokeWidth={2} dot={false} name="Image" />
                  <Line type="monotone" dataKey="audio" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Audio" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Trust Score & Risk Categories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Trust Score Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={MOCK_TRUST_DISTRIBUTION}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="range" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {MOCK_TRUST_DISTRIBUTION.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={
                          index < 2 ? '#ef4444' :
                          index < 4 ? '#f59e0b' :
                          index < 6 ? '#10b981' : '#059669'
                        } />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Risk Category Breakdown</h3>
                <div className="space-y-4">
                  {MOCK_RISK_CATEGORIES.map((risk) => (
                    <div key={risk.category}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{risk.category}</span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{risk.percentage}% ({risk.count})</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-emerald-500 h-3 rounded-full transition-all"
                          style={{ width: `${risk.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* AI & USERS TAB */}
        {activeTab === 'ai-users' && (
          <>
            {/* AI Assessment Trends */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Assessment Trends</h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Provider Filter:</span>
                  </div>
                  <div className="flex gap-2">
                    {['mistral', 'qwen', 'openai'].map((provider) => (
                      <button
                        key={provider}
                        onClick={() => {
                          setSelectedProviders(prev =>
                            prev.includes(provider)
                              ? prev.filter(p => p !== provider)
                              : [...prev, provider]
                          );
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedProviders.includes(provider)
                            ? provider === 'mistral'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                              : provider === 'qwen'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {provider.charAt(0).toUpperCase() + provider.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={MOCK_AI_ASSESSMENTS}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                  <YAxis yAxisId="left" stroke="#6b7280" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#6b7280" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                  />
                  <Legend />
                  {selectedProviders.includes('mistral') && (
                    <Area yAxisId="left" type="monotone" dataKey="mistral" stackId="1" fill="#f97316" stroke="#f97316" name="Mistral" />
                  )}
                  {selectedProviders.includes('qwen') && (
                    <Area yAxisId="left" type="monotone" dataKey="qwen" stackId="1" fill="#a855f7" stroke="#a855f7" name="Qwen" />
                  )}
                  {selectedProviders.includes('openai') && (
                    <Area yAxisId="left" type="monotone" dataKey="openai" stackId="1" fill="#10b981" stroke="#10b981" name="OpenAI" />
                  )}
                  <Line yAxisId="right" type="monotone" dataKey="responseTime" stroke="#6b7280" strokeWidth={2} dot={false} name="Response Time (ms)" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* User/Organization Activity */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User/Organization Activity</h3>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Filter:</span>
                    </div>
                    <select
                      value={userOrgFilter}
                      onChange={(e) => setUserOrgFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Users</option>
                      <option value="organization">Organization Only</option>
                      <option value="individual">Individual Only</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('user')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          User
                          {sortField === 'user' && (sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('uploads')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Uploads
                          {sortField === 'uploads' && (sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('avgTrustScore')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Avg Trust Score
                          {sortField === 'avgTrustScore' && (sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('riskAlerts')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Risk Alerts
                          {sortField === 'riskAlerts' && (sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                        </button>
                      </th>
                      <th className="text-left px-6 py-3">
                        <button onClick={() => handleSort('lastActive')} className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                          Last Active
                          {sortField === 'lastActive' && (sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />)}
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedUserData.map((user) => (
                      <tr key={user.user} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{user.user}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.uploads.toLocaleString()}</td>
                        <td className="px-6 py-4">
                          <span className={`text-sm font-medium ${
                            user.avgTrustScore >= 0.7 ? 'text-emerald-600 dark:text-emerald-400' :
                            user.avgTrustScore >= 0.4 ? 'text-yellow-600 dark:text-yellow-400' :
                            'text-red-600 dark:text-red-400'
                          }`}>
                            {user.avgTrustScore.toFixed(2)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            user.riskAlerts === 0
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : user.riskAlerts <= 2
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {user.riskAlerts}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{formatDate(user.lastActive)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
